import { createContext, useContext, useRef, useCallback, ReactNode } from 'react';

interface RefreshContextType {
  registerRefreshHandler: (route: string, handler: () => Promise<void>) => void;
  unregisterRefreshHandler: (route: string) => void;
  triggerRefresh: (route: string) => Promise<void>;
  registerResetHandler: (handler: () => void) => void;
  resetTestPanel: () => void;
}

const RefreshContext = createContext<RefreshContextType | undefined>(undefined);

export function RefreshProvider({ children }: { children: ReactNode }) {
  const handlersRef = useRef<Map<string, () => Promise<void>>>(new Map());
  const resetHandlerRef = useRef<(() => void) | null>(null);

  const registerRefreshHandler = useCallback((route: string, handler: () => Promise<void>) => {
    handlersRef.current.set(route, handler);
  }, []);

  const unregisterRefreshHandler = useCallback((route: string) => {
    handlersRef.current.delete(route);
  }, []);

  const triggerRefresh = useCallback(async (route: string) => {
    const handler = handlersRef.current.get(route);
    if (handler) {
      await handler();
    }
  }, []);

  const registerResetHandler = useCallback((handler: () => void) => {
    resetHandlerRef.current = handler;
  }, []);

  const resetTestPanel = useCallback(() => {
    if (resetHandlerRef.current) {
      resetHandlerRef.current();
    }
  }, []);

  return (
    <RefreshContext.Provider
      value={{
        registerRefreshHandler,
        unregisterRefreshHandler,
        triggerRefresh,
        registerResetHandler,
        resetTestPanel,
      }}
    >
      {children}
    </RefreshContext.Provider>
  );
}

export function useRefresh() {
  const context = useContext(RefreshContext);
  if (context === undefined) {
    throw new Error('useRefresh must be used within a RefreshProvider');
  }
  return context;
}
